import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useAuth, API } from "@/App";
import { useLayout } from "@/context/LayoutContext";
import { Html5QrcodeScanner } from "html5-qrcode";
import { toast } from "sonner";
import {
  BarChart3,
  Ticket,
  Users,
  Calendar,
  Plus,
  Edit,
  Trash2,
  QrCode,
  Star,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  X,
  Check,
  Camera,
  Keyboard
} from "lucide-react";

const tabs = [
  { id: "overview", label: "Übersicht", icon: BarChart3 },
  { id: "deals", label: "Deals", icon: Ticket },
  { id: "points", label: "Punkte", icon: Star },
  { id: "reservations", label: "Reservierungen", icon: Calendar },
  { id: "users", label: "Kunden", icon: Users },
];

export default function AdminDashboard() {
  const { user, token } = useAuth();
  const { theme } = useLayout();
  const [activeTab, setActiveTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState("aachen");
  
  // Modal states
  const [showDealModal, setShowDealModal] = useState(false);
  const [editingDeal, setEditingDeal] = useState(null);
  const [showPointsModal, setShowPointsModal] = useState(false);
  const [pointsMode, setPointsMode] = useState("add"); // add or redeem
  const [scannerActive, setScannerActive] = useState(false);
  const [memberCode, setMemberCode] = useState("");
  const [memberData, setMemberData] = useState(null);
  const [pointsAmount, setPointsAmount] = useState("");

  useEffect(() => {
    if (user && token && (user.role === "staff" || user.role === "admin")) {
      fetchData();
    }
  }, [user, token, selectedLocation]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, templatesRes, usersRes, reservationsRes] = await Promise.all([
        axios.get(`${API}/admin/stats?location=${selectedLocation}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/coupons/templates/all`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/users?location=${selectedLocation}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/reservations/all?location=${selectedLocation}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(statsRes.data);
      setTemplates(templatesRes.data);
      setUsers(usersRes.data);
      setReservations(reservationsRes.data);
    } catch (e) {
      console.error("Error fetching data:", e);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleLookupMember = async (code) => {
    const lookupCode = code || memberCode;
    if (!lookupCode.trim()) {
      toast.error("Bitte Member-Code eingeben");
      return;
    }

    try {
      const response = await axios.get(`${API}/member/${lookupCode.toUpperCase()}`);
      setMemberData(response.data);
      setScannerActive(false);
    } catch (e) {
      toast.error("Mitglied nicht gefunden");
      setMemberData(null);
    }
  };

  const handlePointsAction = async () => {
    if (!memberData || !pointsAmount || parseInt(pointsAmount) <= 0) {
      toast.error("Bitte gültige Punkte eingeben");
      return;
    }

    try {
      const endpoint = pointsMode === "add" ? "/points/add" : "/points/redeem";
      const response = await axios.post(`${API}${endpoint}`, {
        member_code: memberData.member_code,
        points: parseInt(pointsAmount),
        description: pointsMode === "add" 
          ? `Einkauf: ${pointsAmount} EUR` 
          : "Punkte eingelöst"
      });

      toast.success(response.data.message);
      setMemberData(prev => ({ ...prev, points: response.data.new_balance }));
      setPointsAmount("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const handleDeleteDeal = async (templateId) => {
    if (!confirm("Deal wirklich löschen?")) return;

    try {
      await axios.delete(`${API}/coupons/templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deal gelöscht");
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleReservationStatus = async (reservationId, status) => {
    try {
      await axios.put(`${API}/reservations/${reservationId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Status aktualisiert");
      fetchData();
    } catch (e) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  if (!user || (user.role !== "staff" && user.role !== "admin")) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: theme.colors.bgMain }}
      >
        <p style={{ color: theme.colors.textSecondary }}>Zugriff verweigert</p>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: theme.colors.bgMain }}
    >
      {/* Header */}
      <header 
        className="sticky top-0 z-30 px-4 py-4"
        style={{ 
          backgroundColor: `${theme.colors.bgMain}ee`,
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${theme.colors.border}`
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h1 
            className="text-xl font-bold"
            style={{ color: theme.colors.textPrimary }}
          >
            Dashboard
          </h1>
          
          {/* Location Selector */}
          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="px-4 py-2 rounded-lg text-sm font-medium"
            style={{ 
              backgroundColor: theme.colors.bgCard,
              color: theme.colors.textPrimary,
              border: `1px solid ${theme.colors.border}`
            }}
          >
            <option value="aachen">📍 Aachen</option>
            <option value="mannheim">📍 Mannheim</option>
          </select>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all"
                style={{ 
                  backgroundColor: isActive ? theme.colors.primary : theme.colors.bgCard,
                  color: isActive ? '#FFF' : theme.colors.textSecondary
                }}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </header>

      <div className="p-4">
        {/* Overview Tab */}
        {activeTab === "overview" && stats && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <StatCard 
                title="Kunden" 
                value={stats.total_users} 
                icon={Users}
                theme={theme}
              />
              <StatCard 
                title="Eingelöst" 
                value={stats.total_coupons_redeemed} 
                icon={Ticket}
                theme={theme}
              />
              <StatCard 
                title="Reservierungen" 
                value={stats.pending_reservations} 
                subtitle="Offen"
                icon={Calendar}
                theme={theme}
              />
              <StatCard 
                title="Punkte im Umlauf" 
                value={stats.total_points_in_circulation} 
                icon={Star}
                theme={theme}
              />
            </div>

            {/* Recent Redemptions */}
            <div 
              className="rounded-2xl overflow-hidden"
              style={{ backgroundColor: theme.colors.bgCard }}
            >
              <div className="p-4 border-b" style={{ borderColor: theme.colors.border }}>
                <h3 className="font-bold" style={{ color: theme.colors.textPrimary }}>
                  Letzte Einlösungen
                </h3>
              </div>
              <div className="divide-y" style={{ borderColor: theme.colors.border }}>
                {stats.recent_redemptions?.slice(0, 5).map((item, i) => (
                  <div key={i} className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium" style={{ color: theme.colors.textPrimary }}>
                        {item.title}
                      </p>
                      <p className="text-xs" style={{ color: theme.colors.textSecondary }}>
                        {item.code}
                      </p>
                    </div>
                    <span className="text-xs" style={{ color: theme.colors.textSecondary }}>
                      {item.redeemed_at ? new Date(item.redeemed_at).toLocaleDateString('de-DE') : '-'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Deals Tab */}
        {activeTab === "deals" && (
          <div className="space-y-4">
            <button
              onClick={() => { setEditingDeal(null); setShowDealModal(true); }}
              className="w-full p-4 rounded-xl flex items-center justify-center gap-2 font-bold"
              style={{ backgroundColor: theme.colors.primary, color: '#FFF' }}
            >
              <Plus className="w-5 h-5" />
              Neuen Deal erstellen
            </button>

            {templates
              .filter(t => t.location === selectedLocation)
              .map(template => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="rounded-xl overflow-hidden"
                  style={{ 
                    backgroundColor: theme.colors.bgCard,
                    border: `1px solid ${theme.colors.border}`,
                    opacity: template.active ? 1 : 0.6
                  }}
                >
                  <div className="flex">
                    <img 
                      src={template.image_url} 
                      alt={template.title}
                      className="w-24 h-24 object-cover"
                    />
                    <div className="flex-1 p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold" style={{ color: theme.colors.textPrimary }}>
                            {template.title}
                          </h3>
                          <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                            {template.value}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => { setEditingDeal(template); setShowDealModal(true); }}
                            className="p-2 rounded-lg"
                            style={{ backgroundColor: theme.colors.bgMain }}
                          >
                            <Edit className="w-4 h-4" style={{ color: theme.colors.primary }} />
                          </button>
                          <button
                            onClick={() => handleDeleteDeal(template.id)}
                            className="p-2 rounded-lg"
                            style={{ backgroundColor: theme.colors.bgMain }}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span 
                          className={`text-xs px-2 py-0.5 rounded-full ${template.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                        >
                          {template.active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                        <span className="text-xs" style={{ color: theme.colors.textSecondary }}>
                          {template.redemption_count || 0}x eingelöst
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
          </div>
        )}

        {/* Points Tab */}
        {activeTab === "points" && (
          <div className="space-y-4">
            {/* Mode Toggle */}
            <div 
              className="flex p-1 rounded-xl"
              style={{ backgroundColor: theme.colors.bgCard }}
            >
              <button
                onClick={() => setPointsMode("add")}
                className="flex-1 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: pointsMode === "add" ? theme.colors.primary : 'transparent',
                  color: pointsMode === "add" ? '#FFF' : theme.colors.textSecondary
                }}
              >
                <ArrowUpRight className="w-4 h-4" />
                Punkte vergeben
              </button>
              <button
                onClick={() => setPointsMode("redeem")}
                className="flex-1 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                style={{ 
                  backgroundColor: pointsMode === "redeem" ? theme.colors.primary : 'transparent',
                  color: pointsMode === "redeem" ? '#FFF' : theme.colors.textSecondary
                }}
              >
                <ArrowDownRight className="w-4 h-4" />
                Punkte einlösen
              </button>
            </div>

            {/* Scanner / Input */}
            <div 
              className="rounded-2xl p-4"
              style={{ backgroundColor: theme.colors.bgCard }}
            >
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setScannerActive(!scannerActive)}
                  className="p-3 rounded-xl"
                  style={{ 
                    backgroundColor: scannerActive ? theme.colors.primary : theme.colors.bgMain,
                    color: scannerActive ? '#FFF' : theme.colors.textPrimary
                  }}
                >
                  <Camera className="w-5 h-5" />
                </button>
                <div className="flex-1 flex gap-2">
                  <input
                    type="text"
                    placeholder="Member-Code eingeben..."
                    value={memberCode}
                    onChange={(e) => setMemberCode(e.target.value.toUpperCase())}
                    className="flex-1 px-4 py-3 rounded-xl outline-none"
                    style={{ 
                      backgroundColor: theme.colors.bgMain,
                      color: theme.colors.textPrimary,
                      border: `1px solid ${theme.colors.border}`
                    }}
                  />
                  <button
                    onClick={() => handleLookupMember()}
                    className="px-4 rounded-xl font-medium"
                    style={{ backgroundColor: theme.colors.primary, color: '#FFF' }}
                  >
                    <Search className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* QR Scanner */}
              {scannerActive && (
                <div id="qr-reader" className="rounded-xl overflow-hidden mb-4" />
              )}

              {/* Member Info */}
              {memberData && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: theme.colors.bgMain }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-bold text-lg" style={{ color: theme.colors.textPrimary }}>
                        {memberData.name}
                      </h3>
                      <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                        {memberData.member_code}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-black" style={{ color: theme.colors.primary }}>
                        {memberData.points}
                      </p>
                      <p className="text-xs" style={{ color: theme.colors.textSecondary }}>Punkte</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder={pointsMode === "add" ? "EUR Betrag" : "Punkte"}
                      value={pointsAmount}
                      onChange={(e) => setPointsAmount(e.target.value)}
                      className="flex-1 px-4 py-3 rounded-xl outline-none"
                      style={{ 
                        backgroundColor: theme.colors.bgCard,
                        color: theme.colors.textPrimary,
                        border: `1px solid ${theme.colors.border}`
                      }}
                    />
                    <button
                      onClick={handlePointsAction}
                      className="px-6 py-3 rounded-xl font-bold"
                      style={{ 
                        backgroundColor: pointsMode === "add" ? '#10B981' : '#F59E0B',
                        color: '#FFF'
                      }}
                    >
                      {pointsMode === "add" ? '+' : '-'} {pointsAmount || '0'}
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        )}

        {/* Reservations Tab */}
        {activeTab === "reservations" && (
          <div className="space-y-3">
            {reservations.length === 0 ? (
              <p className="text-center py-8" style={{ color: theme.colors.textSecondary }}>
                Keine Reservierungen
              </p>
            ) : (
              reservations.map(res => (
                <div
                  key={res.id}
                  className="rounded-xl p-4"
                  style={{ 
                    backgroundColor: theme.colors.bgCard,
                    border: `1px solid ${theme.colors.border}`
                  }}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-bold" style={{ color: theme.colors.textPrimary }}>
                        {res.name}
                      </h3>
                      <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                        {res.phone}
                      </p>
                    </div>
                    <span 
                      className={`text-xs px-2 py-1 rounded-full ${
                        res.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        res.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        res.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {res.status === 'pending' ? 'Offen' :
                       res.status === 'confirmed' ? 'Bestätigt' :
                       res.status === 'cancelled' ? 'Storniert' : 'Abgeschlossen'}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm mb-3" style={{ color: theme.colors.textSecondary }}>
                    <span>📅 {res.date}</span>
                    <span>🕐 {res.time}</span>
                    <span>👥 {res.guests} Personen</span>
                  </div>

                  {res.status === 'pending' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleReservationStatus(res.id, 'confirmed')}
                        className="flex-1 py-2 rounded-lg font-medium bg-green-500 text-white flex items-center justify-center gap-1"
                      >
                        <Check className="w-4 h-4" /> Bestätigen
                      </button>
                      <button
                        onClick={() => handleReservationStatus(res.id, 'cancelled')}
                        className="flex-1 py-2 rounded-lg font-medium bg-red-500 text-white flex items-center justify-center gap-1"
                      >
                        <X className="w-4 h-4" /> Stornieren
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="space-y-3">
            {users.map(u => (
              <div
                key={u.id}
                className="rounded-xl p-4 flex items-center justify-between"
                style={{ 
                  backgroundColor: theme.colors.bgCard,
                  border: `1px solid ${theme.colors.border}`
                }}
              >
                <div>
                  <h3 className="font-bold" style={{ color: theme.colors.textPrimary }}>
                    {u.name}
                  </h3>
                  <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
                    {u.email}
                  </p>
                  <p className="text-xs font-mono" style={{ color: theme.colors.textSecondary }}>
                    {u.member_code}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold" style={{ color: theme.colors.primary }}>
                    {u.points || 0}
                  </p>
                  <p className="text-xs" style={{ color: theme.colors.textSecondary }}>Punkte</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Deal Modal */}
      {showDealModal && (
        <DealModal
          deal={editingDeal}
          location={selectedLocation}
          theme={theme}
          token={token}
          onClose={() => { setShowDealModal(false); setEditingDeal(null); }}
          onSave={() => { setShowDealModal(false); setEditingDeal(null); fetchData(); }}
        />
      )}
    </div>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, theme }) {
  return (
    <div 
      className="rounded-xl p-4"
      style={{ 
        backgroundColor: theme.colors.bgCard,
        border: `1px solid ${theme.colors.border}`
      }}
    >
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-5 h-5" style={{ color: theme.colors.primary }} />
      </div>
      <p className="text-2xl font-bold" style={{ color: theme.colors.textPrimary }}>
        {value}
      </p>
      <p className="text-sm" style={{ color: theme.colors.textSecondary }}>
        {title} {subtitle && <span className="text-xs">({subtitle})</span>}
      </p>
    </div>
  );
}

function DealModal({ deal, location, theme, token, onClose, onSave }) {
  const [form, setForm] = useState({
    title: deal?.title || "",
    description: deal?.description || "",
    value: deal?.value || "",
    image_url: deal?.image_url || "",
    valid_days: deal?.valid_days || 7,
    badge_type: deal?.badge_type || "",
    active: deal?.active ?? true,
    location: deal?.location || location
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (deal) {
        await axios.put(`${API}/coupons/templates/${deal.id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Deal aktualisiert");
      } else {
        await axios.post(`${API}/coupons/templates`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Deal erstellt");
      }
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl"
        style={{ backgroundColor: theme.colors.bgCard }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 p-4 flex items-center justify-between border-b" style={{ borderColor: theme.colors.border, backgroundColor: theme.colors.bgCard }}>
          <h2 className="text-xl font-bold" style={{ color: theme.colors.textPrimary }}>
            {deal ? 'Deal bearbeiten' : 'Neuer Deal'}
          </h2>
          <button onClick={onClose}>
            <X className="w-6 h-6" style={{ color: theme.colors.textSecondary }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <input
            type="text"
            placeholder="Titel"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            className="w-full px-4 py-3 rounded-xl outline-none"
            style={{ backgroundColor: theme.colors.bgMain, color: theme.colors.textPrimary, border: `1px solid ${theme.colors.border}` }}
          />
          
          <textarea
            placeholder="Beschreibung"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            required
            rows={3}
            className="w-full px-4 py-3 rounded-xl outline-none resize-none"
            style={{ backgroundColor: theme.colors.bgMain, color: theme.colors.textPrimary, border: `1px solid ${theme.colors.border}` }}
          />

          <input
            type="text"
            placeholder="Wert (z.B. 'Gratis', '20% Rabatt')"
            value={form.value}
            onChange={(e) => setForm({ ...form, value: e.target.value })}
            required
            className="w-full px-4 py-3 rounded-xl outline-none"
            style={{ backgroundColor: theme.colors.bgMain, color: theme.colors.textPrimary, border: `1px solid ${theme.colors.border}` }}
          />

          <input
            type="url"
            placeholder="Bild URL"
            value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            required
            className="w-full px-4 py-3 rounded-xl outline-none"
            style={{ backgroundColor: theme.colors.bgMain, color: theme.colors.textPrimary, border: `1px solid ${theme.colors.border}` }}
          />

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="text-sm mb-1 block" style={{ color: theme.colors.textSecondary }}>
                Gültig (Tage)
              </label>
              <input
                type="number"
                value={form.valid_days}
                onChange={(e) => setForm({ ...form, valid_days: parseInt(e.target.value) })}
                min={1}
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{ backgroundColor: theme.colors.bgMain, color: theme.colors.textPrimary, border: `1px solid ${theme.colors.border}` }}
              />
            </div>
            <div className="flex-1">
              <label className="text-sm mb-1 block" style={{ color: theme.colors.textSecondary }}>
                Badge
              </label>
              <select
                value={form.badge_type}
                onChange={(e) => setForm({ ...form, badge_type: e.target.value })}
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{ backgroundColor: theme.colors.bgMain, color: theme.colors.textPrimary, border: `1px solid ${theme.colors.border}` }}
              >
                <option value="">Kein Badge</option>
                <option value="vip">VIP</option>
                <option value="new">Neu</option>
                <option value="hot">Beliebt</option>
                <option value="exclusive">Exklusiv</option>
                <option value="limited">Begrenzt</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
              className="w-5 h-5 rounded"
            />
            <span style={{ color: theme.colors.textPrimary }}>Deal aktiv</span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 rounded-xl font-bold text-white"
            style={{ backgroundColor: theme.colors.primary }}
          >
            {loading ? 'Speichern...' : (deal ? 'Aktualisieren' : 'Erstellen')}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
